import { Popover as PopoverPrimitive } from 'radix-ui'
import type { ReactNode } from 'react'
import s from './Popover.module.css'

type PopoverProps = {
  trigger: ReactNode
  children: ReactNode
  align?: 'start' | 'end'
  width?: number
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

/**
 * Поповеры компании/уведомлений/профиля в шапке (prototype/Prototype.dc.html:134-245).
 * Клик вне, Escape и позиционирование у края экрана — Radix; в макете это было костылём
 * (`<div style="position:fixed;inset:0;z-index:60">` под каждым поповером) и не переносится.
 *
 * Без ручного `data-align={align}`: `Popover.Content` (через `Popper.Content`) сам проставляет
 * `data-side`/`data-align` на РЕАЛЬНО применённой стороне после коллизии с краем вьюпорта —
 * проверено в исходнике (`node_modules/@radix-ui/react-popper/dist/index.mjs:233-234`). Ручной
 * дубль этого атрибута дал бы неверный `transform-origin`, если Radix перевернёт выравнивание
 * у края экрана: DOM показывал бы `data-align="end"`, а панель на самом деле раскрылась бы влево.
 */
function PopoverRoot({
  trigger,
  children,
  align = 'start',
  width,
  open,
  onOpenChange,
}: PopoverProps) {
  return (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          className={s.content}
          align={align}
          sideOffset={8}
          style={width ? { width } : undefined}
          /*
           * Фокус при открытии встаёт на САМУ ПАНЕЛЬ, а не на первый её элемент.
           *
           * Дефолт Radix — сфокусировать первый tabbable, но с оговоркой: его `FocusScope`
           * вызывает `removeLinks(getTabbableCandidates(container))`, то есть НАМЕРЕННО
           * выбрасывает из кандидатов все `<a>`. В меню профиля первым идёт ссылка «Личный
           * кабинет», а за ней кнопка «Выйти» — и фокус доставался ВЫХОДУ. Разрушительное
           * действие получало фокус первым, в самом частом меню интерфейса. Проверено живьём:
           * `document.activeElement` после открытия — «Выйти».
           *
           * Превратить ссылку в кнопку нельзя: пропадут Ctrl+клик, «открыть в новой вкладке»
           * и роль ссылки в скринридере.
           *
           * Панель фокусируема (`tabIndex: -1` ставит сам Radix), поэтому фокус на ней —
           * законное состояние: ничего не «выбрано», а Tab дальше идёт по порядку DOM, то есть
           * «Личный кабинет» → «Выйти». Так же ведут себя и остальные поповеры: ни в одном из
           * них первый элемент больше не выделяется сам собой.
           */
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            event.currentTarget instanceof HTMLElement && event.currentTarget.focus()
          }}
        >
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}

/**
 * Явная кнопка закрытия ВНУТРИ поповера — крестик в свитчере сервисов
 * (prototype/Prototype.dc.html:251). Это не костыль макета (в отличие от `fixed inset-0`,
 * который не переносим): законный второй способ закрыть, который в макете нарисован.
 *
 * Экспортируется отсюда, а не берётся потребителем напрямую из `radix-ui`: во всей кодовой
 * базе Radix заперт внутри своих обёрток в `shared/ui` (Tooltip, Dialog, DropdownMenu, Switch,
 * Toast) — виджету незачем знать, на чём построен поповер. Расширяем публичный API обёртки,
 * а не обходим её.
 */
const Close = PopoverPrimitive.Close

/** `<Popover trigger={...}>` — корень; `Popover.Close` — кнопка закрытия внутри панели. */
export const Popover = Object.assign(PopoverRoot, { Close })
